/**
 * Owns the utilisation period the user has selected (CWL-157).
 *
 * Both the Teams list and the team detail page need the same control, so the
 * cursor/custom-bounds state and the derived range live here rather than being
 * duplicated in each page.
 */
import { useMemo, useState } from 'react';
import type { UtilisationPeriodSelectorProps } from '../components/UtilisationPeriodSelector';
import {
  type UtilisationPeriod,
  type UtilisationRange,
  type UtilisationRangeQuery,
  resolvePeriodRange,
  stepPeriod,
  toDateInputValue,
  toRangeQuery,
} from '../lib/utilisationPeriod';

/** Props to spread straight onto `<UtilisationPeriodSelector />`. */
type SelectorProps = Omit<UtilisationPeriodSelectorProps, 'showLabel' | 'className'>;

export interface UseUtilisationPeriodResult {
  period: UtilisationPeriod;
  range: UtilisationRange;
  /** The `?from=&to=` pair for the teams endpoints. */
  query: UtilisationRangeQuery;
  selectorProps: SelectorProps;
}

export function useUtilisationPeriod(
  initialPeriod: UtilisationPeriod = 'week',
): UseUtilisationPeriodResult {
  const [period, setPeriod] = useState<UtilisationPeriod>(initialPeriod);
  const [cursor, setCursor] = useState<Date>(() => new Date());
  // Seeded from today so switching to Custom starts on a valid single-day range
  // rather than silently falling back to the current week.
  const [customFrom, setCustomFrom] = useState(() => toDateInputValue(new Date()));
  const [customTo, setCustomTo] = useState(() => toDateInputValue(new Date()));

  const range = useMemo(
    () => resolvePeriodRange(period, cursor, customFrom, customTo),
    [period, cursor, customFrom, customTo],
  );

  const query = useMemo(() => toRangeQuery(range), [range]);

  return {
    period,
    range,
    query,
    selectorProps: {
      period,
      range,
      customFrom,
      customTo,
      onPeriodChange: setPeriod,
      onStep: (direction) => setCursor((current) => stepPeriod(period, current, direction)),
      onCustomFromChange: setCustomFrom,
      onCustomToChange: setCustomTo,
    },
  };
}
